import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FadersIcon } from '@phosphor-icons/react';
import { DARK } from '@protomaps/basemaps';
import { Point } from 'geojson';
import {
  addProtocol,
  GeoJSONSource,
  MapLayerMouseEvent,
  Map as MaplibreMap,
  removeProtocol,
  StyleSpecification,
  setRTLTextPlugin,
} from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import {
  GeolocateControl,
  Layer,
  Map as MapGL,
  MapRef,
  NavigationControl,
  ScaleControl,
  Source,
} from 'react-map-gl/maplibre';
import {
  Button,
  Chip,
  CloseButton,
  Drawer,
  Paper,
  ScrollArea,
  Table,
  Transition,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  buildPMTilesLayers,
  CompassControl,
  FallbackPMTiles,
  SourceControl,
  ToggleControl,
} from '../libs/maplibre';
import { AppFilter } from './filter';
import { Place, Species } from './models';
import { useStore } from './store';

setRTLTextPlugin('https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.js', true);

const SightingsMap = () => {
  //const depts = useStore((state) => state.depts);
  const stats = useStore((state) => state.stats);
  const theme = useStore((state) => state.theme);
  const cluster = useStore((state) => state.cluster);
  const icons = useStore((state) => state.icons);
  const daysDiff = useStore((state) => state.daysDiff);
  const sightingsGeoJson = useStore((state) => state.sightingsGeoJson);
  const sightings = useStore((state) => state.sightings);
  //const loadDepts = useStore((state) => state.loadDepts);
  const setSightingsGeoJson = useStore((state) => state.setSightingsGeoJson);
  const details = useStore((state) => state.details);
  const setDetails = useStore((state) => state.setDetails);
  const resetDetails = useStore((state) => state.resetDetails);
  const setNewBounds = useStore((state) => state.setNewBounds);
  const feature = useStore((state) => state.feature);
  const setFeature = useStore((state) => state.setFeature);
  const forcePlaceDetails = useStore((state) => state.forcePlaceDetails);
  const id_species = useStore((state) => state.id_species);
  const setForcePlaceDetails = useStore((state) => state.setForcePlaceDetails);
  const initDates = useStore((state) => state.initDates);
  const threshold = useStore((state) => state.threshold);
  const bounds = useStore((state) => state.bounds);
  const range = useStore((state) => state.range);
  const fetchData = useStore((state) => state.fetchData);
  const fetchYearlySeries = useStore((state) => state.fetchYearlySeries);
  const toggleCluster = useStore((state) => state.toggleCluster);
  const toggleIcons = useStore((state) => state.toggleIcons);
  // breakpoing sm dans le AppShell.Navbar est à 48em: https://mantine.dev/styles/responsive/#configure-breakpoints
  const mobile = window.matchMedia('(max-width: 48em)').matches;

  const [cursor, setCursor] = useState<string>('auto');
  const [filterOpened, { toggle: toggleFilter }] = useDisclosure(!mobile);

  useEffect(() => {
    initDates(6);
  }, [stats]);

  useEffect(() => {
    if (range) {
      fetchData(true, true);
    }
  }, [range, id_species, threshold]);

  useEffect(() => {
    if (stats && id_species.length === 0) {
      fetchData(true, false);
    }
  }, [bounds]);

  useEffect(() => {
    fetchYearlySeries();
  }, [stats, id_species]);

  const mapStyle = useMemo<StyleSpecification>(
    () => ({
      version: 8,
      glyphs: 'https://tiles.shagr.at/basemaps-assets/fonts/{fontstack}/{range}.pbf',
      sprite: [
        {
          id: 'default',
          url: `https://tiles.shagr.at/basemaps-assets/sprites/v4/${theme === DARK ? 'dark' : 'light'}`,
        },
        ...(icons
          ? [
              {
                id: 'birds',
                url: `${window.location.href}birds/sprites`,
              },
            ]
          : []),
      ],
      sources: {
        pmtiles: {
          type: 'vector',
          maxzoom: 15,
          url: 'pmtiles://https://tuiles-01.s3.gra.io.cloud.ovh.net/france-z15.pmtiles',
          attribution:
            '<a href="https://github.com/protomaps/basemaps">Protomaps</a> © <a href="https://openstreetmap.org">OpenStreetMap</a>',
        },
        arcgisonline: {
          type: 'raster',
          tileSize: 256,
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          ],
          attribution:
            'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        },
      },
      layers: [
        {
          id: 'arcgisonline',
          type: 'raster',
          source: 'arcgisonline',
          layout: {
            visibility: 'none',
          },
        },
        //...basemapkitStyle(theme == DARK ? "avenue-night" : "avenue", { source: "pmtiles", lang: "fr", fontName: "Jost" }),
        ...buildPMTilesLayers('pmtiles', theme, {
          lang: 'fr',
          disableRoadShields: true,
          regular: 'Jost Regular',
          bold: 'Jost Medium',
          italic: 'Jost Italic',
        }),
      ],
    }),
    [theme, icons]
  );

  useEffect(() => {
    const protocol = new Protocol();
    protocol.add(
      new FallbackPMTiles(
        'https://tuiles-01.s3.gra.io.cloud.ovh.net/france-z15.pmtiles',
        'https://tuiles-01.s3.gra.io.cloud.ovh.net/world-z10.pmtiles'
      )
    );
    addProtocol('pmtiles', protocol.tile);
    //loadDepts();
    return () => {
      removeProtocol('pmtiles');
    };
  }, []);

  useEffect(() => {
    const colors = ['#f03e3e', '#ae3ec9', '#4263eb', '#1098ad', '#37b24d', '#f59f00'];
    setSightingsGeoJson({
      type: 'FeatureCollection',
      features: sightings
        .filter((sighting) => stats?.speciesMapPerId[sighting.species])
        .map((sighting) => {
          const species: Species = stats!.speciesMapPerId[sighting.species];
          const place: Place = sighting.expand?.place;
          const pos = id_species.indexOf(species.id_species);
          let sprite = 'birds:005-bird';
          if (species.latin_name === 'Alcedo atthis') {
            sprite = 'birds:022-kingfisher';
          } else if (species.name.indexOf('Mésange') > -1) {
            sprite = 'birds:006-chickadee';
          } else if (species.name.indexOf('Ibis') > -1 || species.name.indexOf('Spatule') > -1) {
            sprite = 'birds:043-spoonbill';
          } else if (species.name.indexOf('Héron') > -1) {
            sprite = 'birds:014-heron';
          } else if (species.name.indexOf('Moineau') > -1) {
            sprite = 'birds:042-sparrow';
          } else if (species.latin_name && species.latin_name.indexOf('Upupa') > -1) {
            sprite = 'birds:017-hoopoe';
          } else if (species.name.indexOf('Canard') > -1) {
            sprite = 'birds:009-duck';
          } else if (species.name.indexOf('Corbeau') > -1) {
            sprite = 'birds:037-raven';
          } else if (
            species.name.indexOf('Pigeon') > -1 ||
            species.name.indexOf('Tourterelle') > -1
          ) {
            sprite = 'birds:035-pigeon';
          }
          return {
            type: 'Feature',
            id: sighting.id,
            properties: {
              name: species.name,
              place: place.id,
              place_name: place.name,
              moment: sighting.moment,
              latitude: place.latitude,
              longitude: place.longitude,
              count: sighting.count,
              id_species: species.id_species,
              label: stats?.speciesMapPerIdSpecies[species.id_species].label,
              color: pos > -1 ? colors[pos % colors.length] : 'red',
              //age: dayjs(sighting.moment).diff(sightings[0].moment, "day"),
              sprite,
            },
            geometry: {
              type: 'Point',
              coordinates: [place.longitude, place.latitude],
            },
          };
        }),
    });
  }, [sightings]);

  const mapRef = useRef<MapRef>(null);
  const onClick = async (event: MapLayerMouseEvent) => {
    setFeature(event.features && event.features[0]);
  };

  const closeDrawer = () => {
    if (details && details.length > 0) {
      mapRef.current?.flyTo({
        padding: mobile
          ? {
              bottom: 0,
            }
          : {
              right: 0,
            },
        speed: 1.2,
      });
    }
    resetDetails();
  };

  useEffect(() => {
    const zoomOnFeature = async () => {
      if (!feature) {
        closeDrawer();
        return;
      }

      if (feature.properties.name) {
        if (!details || details.length === 0) {
          mapRef.current?.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            padding: mobile
              ? {
                  bottom: 250,
                }
              : {
                  right: 250,
                },
            speed: 1.2,
          });
        }
        setDetails(true);
      } else {
        const clusterId = feature.properties.cluster_id;
        const geoSource = mapRef.current?.getSource('sightings') as GeoJSONSource;
        const zoom = await geoSource.getClusterExpansionZoom(clusterId);

        mapRef.current?.easeTo({
          center: (feature.geometry as Point).coordinates as [number, number],
          zoom: zoom ? zoom : undefined,
          duration: 500,
        });
      }
    };
    zoomOnFeature();
  }, [feature]);

  const onMouseEnter = (event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
    if (!feature) {
      return;
    }
    if (feature.properties.name) {
      //onClick(event);
      setCursor('pointer');
    }
  };

  const onMouseLeave = (event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
    if (!feature) {
      return;
    }
    if (feature.properties.name) {
      //onClick(event);
      setCursor('auto');
    }
  };

  const onNewBounds = () => {
    if (mapRef.current) {
      setNewBounds(mapRef.current);
      const map: MaplibreMap | undefined = mapRef?.current?.getMap();
      map?.scrollZoom?.setZoomRate(1 / 50); // vitesse zoom trackpad : 2 fois plus rapide que la valeur par défaut
      map?.scrollZoom?.setWheelZoomRate(1 / 225); // vitesse zoom scroll souris : 2 fois plus rapide que la valeur par défaut
    }
  };

  return (
    mapStyle && (
      <MapGL
        interactiveLayerIds={['clusters', 'unclustered-point-radius']}
        onLoad={onNewBounds}
        onClick={onClick}
        onMoveEnd={onNewBounds}
        cursor={cursor}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        //onMouseLeave={() => setPopupInfo(null)}
        ref={mapRef}
        initialViewState={{
          longitude: -1.5743799,
          latitude: 47.2501129,
          zoom: 9,
          pitch: 0,
          bearing: 0,
          padding: {
            top: 0,
            left: mobile ? 0 : 300,
            right: 0,
            bottom: 0,
          },
        }}
        attributionControl={{ compact: true }}
        style={{ width: '100%', flex: 1, height: 'inherit' }}
        mapStyle={mapStyle}
      >
        <Transition
          mounted={filterOpened && !!stats}
          transition="scale-y"
          duration={300}
          timingFunction="ease"
        >
          {(transitionStyle) => (
            <Paper
              pos="absolute"
              shadow="xl"
              radius="md"
              withBorder
              m="md"
              p="sm"
              miw="300px"
              maw="300px"
              bg={theme === DARK ? 'dark.9' : 'gray.1'}
              component={ScrollArea}
              type="never"
              style={{
                ...transitionStyle,
                zIndex: 10,
                top: 0,
                bottom: 0,
              }}
            >
              <CloseButton
                variant="transparent"
                size="lg"
                onClick={toggleFilter}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 5,
                  zIndex: 25,
                }}
              />
              <AppFilter />
            </Paper>
          )}
        </Transition>
        <Transition
          mounted={!filterOpened && !!stats}
          transition="fade-up"
          duration={300}
          timingFunction="ease"
        >
          {(transitionStyle) => (
            <Paper
              m="md"
              shadow="md"
              radius="md"
              miw="300px"
              maw="300px"
              withBorder
              bg={theme === DARK ? 'dark.9' : 'gray.1'}
              style={{
                ...transitionStyle,
                top: 0,
                left: 0,
                zIndex: 100000,
                position: 'absolute',
                display: 'flex',
                justifyContent: 'end',
              }}
            >
              <Button
                w="100%"
                radius="md"
                variant="subtle"
                leftSection={<FadersIcon size={20} />}
                onClick={() => toggleFilter()}
              >
                {id_species.length === 0
                  ? 'Toutes espèces'
                  : id_species.length === 1
                    ? stats?.species.find((s) => s.id_species === id_species[0])?.name
                    : `${id_species.length} espèces`}{' '}
                : {daysDiff + 1} jour
                {daysDiff < 1 ? '' : 's'}
              </Button>
            </Paper>
          )}
        </Transition>
        <NavigationControl visualizePitch showCompass position="top-right" />
        <GeolocateControl
          position="top-right"
          trackUserLocation
          positionOptions={{ enableHighAccuracy: true }}
        />
        {/*<GlobeControl position="top-right" />*/}
        <CompassControl position="top-right" />
        <SourceControl position="top-right" sources={['pmtiles', 'arcgisonline']} />
        <ToggleControl
          position="top-right"
          toggle={toggleCluster}
          className={`maplibregl-ctrl-cluster-${cluster ? 'on' : 'off'}`}
        />
        <ToggleControl
          position="top-right"
          toggle={toggleIcons}
          className={`maplibregl-ctrl-icons-${icons ? 'on' : 'off'}`}
        />
        {/*depts &&
          depts.map((dep, i) => (
            <Source key={i} type="geojson" id={`departement-${i}`} data={dep}>
              <Layer
                {...{
                  id: "departement-" + i,
                  source: `departement-${i}`,
                  type: "line",
                  layout: {
                    "line-join": "round",
                    "line-cap": "round",
                  },
                  paint: {
                    "line-color": theme == DARK ? "#03fcd7" : "black",
                    "line-opacity": 0.5,
                    "line-width": 1,
                  },
                }}
              />
            </Source>
          ))*/}
        <ScaleControl unit="metric" position="bottom-right" />
        {sightingsGeoJson && cluster && (
          <Source
            id="sightings"
            cluster
            clusterMaxZoom={14}
            clusterRadius={50}
            type="geojson"
            data={sightingsGeoJson}
          >
            <Layer
              {...{
                id: 'clusters',
                type: 'circle',
                source: 'sightings',
                filter: ['has', 'point_count'],
                paint: {
                  'circle-color': [
                    'step',
                    ['get', 'point_count'],
                    '#51bbd6',
                    100,
                    '#f1f075',
                    750,
                    '#f28cb1',
                  ],
                  'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
                },
              }}
            />
            <Layer
              {...{
                id: 'cluster-count',
                type: 'symbol',
                source: 'sightings',
                filter: ['has', 'point_count'],
                layout: {
                  'text-field': '{point_count_abbreviated}',
                  'text-font': [theme.regular || 'Jost Regular'],
                  'text-size': 12,
                },
                paint: {
                  'text-color': 'black',
                },
              }}
            />
            <Layer
              {...{
                id: 'unclustered-point',
                type: 'circle',
                source: 'sightings',
                filter: ['!', ['has', 'point_count']],
                paint: {
                  'circle-color': 'red', // '#11b4da'
                  'circle-radius': 8,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#fff',
                },
              }}
            />
            <Layer
              {...{
                id: 'unclustered-point-radius',
                type: 'circle',
                source: 'sightings',
                filter: ['!', ['has', 'point_count']],
                paint: {
                  'circle-opacity': 0,
                  'circle-radius': 25,
                },
              }}
            />
          </Source>
        )}
        {sightingsGeoJson && !cluster && !icons && (
          <Source id="sightings" type="geojson" data={sightingsGeoJson}>
            <Layer
              {...{
                id: 'unclustered-point',
                type: 'circle',
                source: 'sightings',
                filter: ['!', ['has', 'point_count']],
                paint: {
                  'circle-color': ['get', 'color'], // '#11b4da'
                  'circle-radius': 8,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#fff',
                },
              }}
            />
            <Layer
              {...{
                id: 'unclustered-point-radius',
                type: 'circle',
                source: 'sightings',
                filter: ['!', ['has', 'point_count']],
                paint: {
                  'circle-opacity': 0,
                  'circle-radius': 25,
                },
              }}
            />
            <Layer
              {...{
                id: 'unclustered-point-labels',
                type: 'symbol',
                source: 'sightings',
                filter: ['!', ['has', 'point_count']],
                layout: {
                  'text-field': '{label}',
                  'text-font': [theme.regular || 'Jost Regular'],
                  'text-size': 10,
                  'text-offset': [0, 1.5],
                },
                paint: {
                  'text-color': theme === DARK ? 'white' : 'black',
                  'text-halo-color': theme === DARK ? 'black' : 'white',
                  'text-halo-width': 0.8,
                },
              }}
            />
          </Source>
        )}
        {sightingsGeoJson && !cluster && icons && (
          <Source id="sightings" type="geojson" data={sightingsGeoJson}>
            <Layer
              {...{
                id: 'unclustered-point-radius',
                type: 'symbol',
                source: 'sightings',
                filter: ['!', ['has', 'point_count']],
                layout: {
                  'icon-image': ['get', 'sprite'],
                  'icon-size': 0.08,
                  'text-field': '{label}',
                  'text-font': [theme.regular || 'Jost Regular'],
                  'text-size': 9,
                  'text-offset': [0, 1.5],
                },
                paint: {
                  'text-color': theme === DARK ? 'white' : 'black',
                  'text-halo-color': theme === DARK ? 'black' : 'white',
                  'text-halo-width': 0.8,
                },
              }}
            />
          </Source>
        )}
        <Drawer
          position={!mobile ? 'right' : 'bottom'}
          size="xs"
          opened={details && details.length > 0}
          onClose={closeDrawer}
          title={details && details.length > 0 ? details[0].expand?.place?.name : ''}
          styles={{
            header: {
              backgroundColor: 'inherit',
            },
            content: {
              backgroundColor: `var(--mantine-color-${theme === DARK ? 'dark-8' : 'gray-1'})`,
              boxShadow: `${mobile ? '0px -2px' : '-2px -0px '} px var(--mantine-color-${
                theme === DARK ? 'dark-9' : 'gray-2'
              })`,
              ...(mobile
                ? {
                    height: '250px',
                  }
                : {}),
            },
            title: {
              fontWeight: 'bold',
            },
          }}
        >
          <Table striped withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Chip
                    disabled={id_species.length === 0}
                    checked={id_species.length !== 0 && !forcePlaceDetails}
                    onChange={(checked) => setForcePlaceDetails(!checked)}
                  >
                    Espèce(s)
                  </Chip>
                </Table.Th>
                <Table.Th>Observation</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody fz="12">
              {details.map((detail) => (
                <Table.Tr key={detail.id}>
                  <Table.Td>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {stats?.speciesMapPerId[detail.species]?.name}{' '}
                      {detail.count && detail.count > 1 ? `(${detail.count})` : ''}
                    </div>
                  </Table.Td>
                  <Table.Td
                    display="flex"
                    style={{
                      flexDirection: 'column',
                    }}
                  >
                    <span>
                      {dayjs(detail.moment).format('DD/MM/YYYY HH:mm').replace(' 00:00', '')}
                    </span>
                    {detail.detail ?? <span>{detail.detail}</span>}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Drawer>
      </MapGL>
    )
  );
};

export default SightingsMap;
